// Story Survey Logic - Typeform-ähnliche Navigation und Verzweigungen

class StorySurveyLogic {
  constructor() {
    this.container = document.querySelector('.story-survey-container');
    if (!this.container) return;
    
    this.form = this.container.querySelector('.story-survey-form');
    this.questions = this.container.querySelectorAll('.survey-question');
    this.currentQuestion = 0;
    this.answers = {};
    this.questionFlow = [];
    
    this.init();
  }

  init() {
    this.setupNavigation();
    this.setupProgressTracking();
    this.setupAutoSave();
    this.setupAbandonTracking();
    this.loadSavedProgress();
    this.showQuestion(0);
    
    // Auto-save bei jeder Antwort
    this.form.addEventListener('change', () => this.saveProgress());
  }
  
  setupAutoSave() {
    // Save progress every 30 seconds
    setInterval(() => {
      if (Object.keys(this.answers).length > 0) {
        this.saveProgressToKlaviyo();
      }
    }, 30000);
  }
  
  setupAbandonTracking() {
    // Track when user leaves the page
    window.addEventListener('beforeunload', () => {
      if (Object.keys(this.answers).length > 0 && !this.surveyCompleted) {
        // Use sendBeacon for reliable tracking even when page closes
        const urlParams = new URLSearchParams(window.location.search);
        const data = {
          event: 'Story Survey Abandoned',
          properties: {
            'order_id': urlParams.get('order_id') || '',
            'story_id': urlParams.get('story_id') || '',
            'customer_id': urlParams.get('customer_id') || '',
            'email': urlParams.get('email') || '',
            'last_question': this.currentQuestion,
            'answers_provided': { ...this.answers },
            'progress_percentage': Math.round((Object.keys(this.answers).length / 8) * 100),
            'time_spent_seconds': Math.round((Date.now() - parseInt(sessionStorage.getItem('survey_start_time'))) / 1000)
          }
        };
        
        // Try to send via beacon API (works even when page closes)
        if (navigator.sendBeacon) {
          navigator.sendBeacon('https://a.klaviyo.com/api/track', JSON.stringify(data));
        }
      }
    });
  }
  
  saveProgressToKlaviyo() {
    if (typeof klaviyo === 'undefined') return;
    
    const urlParams = new URLSearchParams(window.location.search);
    klaviyo.push(['track', 'Story Survey Progress Saved', {
      'order_id': urlParams.get('order_id') || '',
      'story_id': urlParams.get('story_id') || '',
      'customer_id': urlParams.get('customer_id') || '',
      'email': urlParams.get('email') || '',
      'answers_so_far': { ...this.answers },
      'current_question': this.currentQuestion,
      'progress_percentage': Math.round((Object.keys(this.answers).length / 8) * 100),
      'timestamp': new Date().toISOString()
    }]);
  }

  setupNavigation() {
    const nextBtn = this.container.querySelector('.btn-next');
    const backBtn = this.container.querySelector('.btn-back');
    const submitBtn = this.container.querySelector('.btn-submit');
    
    nextBtn?.addEventListener('click', () => this.nextQuestion());
    backBtn?.addEventListener('click', () => this.prevQuestion());
    submitBtn?.addEventListener('click', (e) => this.submitSurvey(e));
    
    // Enter-Taste für nächste Frage
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        const activeTextarea = document.activeElement.tagName === 'TEXTAREA';
        if (!activeTextarea) {
          e.preventDefault();
          this.nextQuestion();
        }
      }
    });
    
    // Auto-advance für Single-Choice-Fragen
    this.setupAutoAdvance();
  }
  
  setupAutoAdvance() {
    // Finde alle Radio-Buttons in Elementen mit data-auto-next
    this.container.querySelectorAll('[data-auto-next="true"] input[type="radio"]').forEach(input => {
      input.addEventListener('change', () => {
        if (input.checked) {
          // Visuelles Feedback
          const option = input.closest('.scale-option, .nps-option, .choice-button, .option-card-large');
          if (option) {
            option.classList.add('selected-flash');
            
            // Füge CSS für Flash-Animation hinzu, falls noch nicht vorhanden
            if (!document.getElementById('auto-advance-styles')) {
              const style = document.createElement('style');
              style.id = 'auto-advance-styles';
              style.textContent = `
                @keyframes selected-flash {
                  0% { transform: scale(1); }
                  50% { transform: scale(1.05); box-shadow: 0 0 20px rgba(139, 90, 131, 0.5); }
                  100% { transform: scale(1); }
                }
                .selected-flash {
                  animation: selected-flash 0.5s ease;
                }
              `;
              document.head.appendChild(style);
            }
          }
          
          // Automatisch zur nächsten Frage nach kurzer Verzögerung
          setTimeout(() => {
            this.saveCurrentAnswer();
            this.nextQuestion();
          }, 600);
        }
      });
    });
  }

  showQuestion(index) {
    // Verstecke alle Fragen
    this.questions.forEach(q => {
      q.classList.remove('active');
      q.style.display = 'none';
    });
    
    // Zeige aktuelle Frage
    const question = this.questions[index];
    if (!question) return;
    
    // Prüfe Bedingungen
    if (this.shouldShowQuestion(question)) {
      question.style.display = 'block';
      setTimeout(() => question.classList.add('active'), 10);
      this.currentQuestion = index;
      
      // Update Navigation
      this.updateNavigation();
      this.updateProgress();
      
      // Fokus auf erstes Input
      const firstInput = question.querySelector('input, textarea, select');
      if (firstInput) {
        setTimeout(() => firstInput.focus(), 400);
      }
    } else {
      // Überspringe zur nächsten Frage
      this.showQuestion(index + 1);
    }
  }

  shouldShowQuestion(question) {
    const showIf = question.dataset.showIf;
    if (!showIf) return true;
    
    const [dependency, condition] = showIf.split(':');
    const dependencyValue = this.answers[dependency];
    
    // Verschiedene Bedingungstypen
    if (condition.startsWith('<=')) {
      return parseInt(dependencyValue) <= parseInt(condition.substring(2));
    } else if (condition.startsWith('>=')) {
      return parseInt(dependencyValue) >= parseInt(condition.substring(2));
    } else if (condition.startsWith('<')) {
      return parseInt(dependencyValue) < parseInt(condition.substring(1));
    } else if (condition.startsWith('>')) {
      return parseInt(dependencyValue) > parseInt(condition.substring(1));
    } else if (condition.includes(',')) {
      // Multiple Werte
      return condition.split(',').includes(dependencyValue);
    } else {
      return dependencyValue === condition;
    }
  }

  nextQuestion() {
    if (!this.validateCurrentQuestion()) return;
    
    // Speichere Antwort
    this.saveCurrentAnswer();
    
    // Finde nächste gültige Frage
    let nextIndex = this.currentQuestion + 1;
    while (nextIndex < this.questions.length) {
      const question = this.questions[nextIndex];
      if (this.shouldShowQuestion(question)) {
        this.questionFlow.push(this.currentQuestion);
        this.showQuestion(nextIndex);
        return;
      }
      nextIndex++;
    }
    
    // Keine weiteren Fragen - Submit
    if (nextIndex >= this.questions.length) {
      this.showQuestion(this.questions.length - 1); // Thank you page
      this.container.querySelector('.btn-next').style.display = 'none';
      this.container.querySelector('.btn-submit').style.display = 'block';
    }
  }

  prevQuestion() {
    if (this.questionFlow.length > 0) {
      const prevIndex = this.questionFlow.pop();
      this.showQuestion(prevIndex);
    }
  }

  validateCurrentQuestion() {
    const question = this.questions[this.currentQuestion];
    const questionId = question.dataset.questionId;
    
    // Skip validation for checkbox questions (optional)
    if (questionId === 'rating_followup' || questionId === 'rating_followup_positive') {
      return true;
    }
    
    // Check for matrix questions
    const matrixRows = question.querySelectorAll('.matrix-row');
    if (matrixRows.length > 0) {
      // Validate each matrix row has an answer
      for (let row of matrixRows) {
        const checked = row.querySelector('input:checked');
        if (!checked) {
          this.showError('Bitte beantworte alle Fragen');
          return false;
        }
      }
      return true;
    }
    
    // Regular validation for other questions
    const requiredInputs = question.querySelectorAll('[required]');
    
    for (let input of requiredInputs) {
      if (input.type === 'radio') {
        const name = input.name;
        const checked = question.querySelector(`[name="${name}"]:checked`);
        if (!checked) {
          this.showError('Bitte wähle eine Option aus');
          return false;
        }
      } else if (input.type === 'checkbox') {
        // Checkboxes are optional, skip validation
        continue;
      } else if (!input.value.trim()) {
        this.showError('Bitte fülle dieses Feld aus');
        input.focus();
        return false;
      }
    }
    
    return true;
  }

  saveCurrentAnswer() {
    const question = this.questions[this.currentQuestion];
    const questionId = question.dataset.questionId;
    
    // Skip start screen
    if (questionId === 'start_screen') return;
    
    const previousAnswer = this.answers[questionId];
    
    // Radio/Checkbox
    const checkedInputs = question.querySelectorAll('input:checked');
    if (checkedInputs.length > 0) {
      if (checkedInputs.length === 1) {
        this.answers[questionId] = checkedInputs[0].value;
      } else {
        this.answers[questionId] = Array.from(checkedInputs).map(i => i.value);
      }
    }
    
    // Text/Textarea
    const textInputs = question.querySelectorAll('input[type="text"], textarea');
    textInputs.forEach(input => {
      if (input.value) {
        this.answers[input.name] = input.value;
      }
    });
    
    // Matrix-Fragen
    const matrixRows = question.querySelectorAll('.matrix-row');
    matrixRows.forEach(row => {
      const checked = row.querySelector('input:checked');
      if (checked) {
        this.answers[checked.name] = checked.value;
      }
    });
    
    // Track answer in Klaviyo immediately
    if (this.answers[questionId] && this.answers[questionId] !== previousAnswer) {
      this.trackAnswerInKlaviyo(questionId);
    }
  }
  
  trackAnswerInKlaviyo(questionId) {
    if (typeof klaviyo === 'undefined') return;
    
    const urlParams = new URLSearchParams(window.location.search);
    const eventData = {
      'order_id': urlParams.get('order_id') || '',
      'story_id': urlParams.get('story_id') || '',
      'customer_id': urlParams.get('customer_id') || '',
      'email': urlParams.get('email') || '',
      'question_id': questionId,
      'answer': this.answers[questionId],
      'all_answers_so_far': { ...this.answers },
      'progress_percentage': Math.round((Object.keys(this.answers).length / 8) * 100),
      'timestamp': new Date().toISOString()
    };
    
    // Track individual answer
    klaviyo.push(['track', 'Story Survey Answer Submitted', eventData]);
    
    // Also update profile with latest answers
    klaviyo.push(['identify', {
      '$email': urlParams.get('email') || '',
      'story_survey_progress': this.answers,
      'story_survey_last_answer': questionId,
      'story_survey_last_update': new Date().toISOString()
    }]);
  }

  updateProgress() {
    // Berechne tatsächlichen Fortschritt
    const totalQuestions = Array.from(this.questions).filter(q => 
      q.dataset.questionId !== 'thank_you'
    ).length;
    
    const currentQuestionIndex = Array.from(this.questions).filter((q, i) => 
      i <= this.currentQuestion && q.dataset.questionId !== 'thank_you'
    ).length;
    
    const progress = ((currentQuestionIndex - 1) / totalQuestions) * 100;
    
    // Update Progress Bar
    const progressFill = this.container.querySelector('.progress-fill');
    if (progressFill) {
      progressFill.style.width = `${Math.max(0, progress)}%`;
    }
    
    // Update Text Counter
    const currentQuestionSpan = this.container.querySelector('.current-question');
    const totalQuestionsSpan = this.container.querySelector('.total-questions');
    
    if (currentQuestionSpan && totalQuestionsSpan) {
      currentQuestionSpan.textContent = Math.min(currentQuestionIndex, totalQuestions);
      totalQuestionsSpan.textContent = totalQuestions;
    }
  }

  updateNavigation() {
    const backBtn = this.container.querySelector('.btn-back');
    const nextBtn = this.container.querySelector('.btn-next');
    const submitBtn = this.container.querySelector('.btn-submit');
    
    // Back Button
    if (backBtn) {
      backBtn.style.display = this.questionFlow.length > 0 ? 'block' : 'none';
    }
    
    // Next/Submit Button
    const isLastQuestion = this.currentQuestion === this.questions.length - 1;
    if (nextBtn) nextBtn.style.display = isLastQuestion ? 'none' : 'block';
    if (submitBtn) submitBtn.style.display = isLastQuestion ? 'block' : 'none';
  }

  showError(message) {
    // Zeige Fehlermeldung
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff4444;
      color: white;
      padding: 12px 24px;
      border-radius: 8px;
      z-index: 1000;
      animation: slideIn 0.3s ease;
    `;
    
    document.body.appendChild(errorDiv);
    setTimeout(() => errorDiv.remove(), 3000);
  }

  saveProgress() {
    const surveyData = {
      currentQuestion: this.currentQuestion,
      answers: this.answers,
      questionFlow: this.questionFlow,
      timestamp: Date.now()
    };
    
    localStorage.setItem('story_survey_progress', JSON.stringify(surveyData));
  }

  loadSavedProgress() {
    const saved = localStorage.getItem('story_survey_progress');
    if (!saved) return;
    
    try {
      const data = JSON.parse(saved);
      // Nur laden wenn nicht zu alt (24 Stunden)
      if (Date.now() - data.timestamp < 24 * 60 * 60 * 1000) {
        this.answers = data.answers || {};
        this.questionFlow = data.questionFlow || [];
        
        // Restore answers to form
        Object.entries(this.answers).forEach(([key, value]) => {
          const input = this.form.querySelector(`[name="${key}"][value="${value}"]`);
          if (input) input.checked = true;
          
          const textInput = this.form.querySelector(`[name="${key}"]:not([type="radio"]):not([type="checkbox"])`);
          if (textInput) textInput.value = value;
        });
        
        // Resume at last question
        if (data.currentQuestion > 0) {
          this.showQuestion(data.currentQuestion);
        }
      }
    } catch (e) {
      console.error('Error loading saved progress:', e);
    }
  }

  async submitSurvey(e) {
    e.preventDefault();
    
    // Finale Validierung
    this.saveCurrentAnswer();
    
    // Berechne Metriken
    const metrics = this.calculateMetrics();
    
    // Sende an Klaviyo
    if (typeof klaviyo !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      
      klaviyo.push(['track', 'Completed Story Feedback Survey', {
        // Meta
        'order_id': urlParams.get('order_id') || '',
        'story_id': urlParams.get('story_id') || '',
        'customer_id': urlParams.get('customer_id') || '',
        
        // Hauptmetriken
        'overall_rating': this.answers.overall_rating,
        'would_recommend': this.answers.would_recommend,
        'story_quality': this.answers.story_quality,
        'personalization_correct': this.answers.personalization_correct,
        
        // Audio Feedback
        'sfx_quality': this.answers.sfx_quality,
        'sfx_timing': this.answers.sfx_timing,
        'audio_music': this.answers.audio_music,
        'audio_voice': this.answers.audio_voice,
        
        // Probleme/Likes
        'issues': this.answers.issues || [],
        'likes': this.answers.likes || [],
        
        // Freitext
        'personalization_issues': this.answers.personalization_issues || '',
        'improvement_suggestions': this.answers.improvement_suggestions || '',
        
        // Berechnete Metriken
        'satisfaction_category': metrics.satisfactionCategory,
        'has_issues': metrics.hasIssues,
        'audio_score': metrics.audioScore,
        'would_recommend_bool': this.answers.would_recommend === 'yes',
        
        // Tracking
        'completion_time_seconds': metrics.completionTime,
        'questions_answered': Object.keys(this.answers).length,
        'device': this.getDevice(),
        'timestamp': new Date().toISOString()
      }]);
    }
    
    // Clear saved data
    localStorage.removeItem('story_survey_progress');
    
    // Show thank you
    this.showQuestion(this.questions.length - 1);
    
    // Show reward box (always visible now)
    const rewardBox = this.container.querySelector('.reward-box');
    if (rewardBox) rewardBox.style.display = 'block';
  }

  calculateMetrics() {
    const rating = parseInt(this.answers.overall_rating) || 0;
    
    return {
      satisfactionCategory: rating <= 3 ? 'low' : rating <= 7 ? 'medium' : 'high',
      hasIssues: this.answers.issues && this.answers.issues.length > 0,
      audioScore: this.calculateAudioScore(),
      completionTime: Math.round((Date.now() - (parseInt(sessionStorage.getItem('survey_start_time')) || Date.now())) / 1000)
    };
  }

  calculateAudioScore() {
    const scores = {
      'excellent': 3,
      'good': 2,
      'perfect': 3,
      'fitting': 3,
      'unclear': 1,
      'distracting': 1,
      'too_much': 1,
      'too_little': 1,
      'missing': 1
    };
    
    const audioAnswers = [
      this.answers.audio_soundeffects,
      this.answers.audio_music,
      this.answers.audio_voice
    ].filter(Boolean);
    
    if (audioAnswers.length === 0) return null;
    
    const totalScore = audioAnswers.reduce((sum, answer) => sum + (scores[answer] || 2), 0);
    return Math.round((totalScore / (audioAnswers.length * 3)) * 5);
  }

  getDevice() {
    const width = window.innerWidth;
    if (width < 768) return 'mobile';
    if (width < 1024) return 'tablet';
    return 'desktop';
  }
}

// Copy Code Function
function copyCode(code) {
  navigator.clipboard.writeText(code).then(() => {
    const btn = event.target;
    const originalText = btn.textContent;
    btn.textContent = 'Kopiert!';
    setTimeout(() => btn.textContent = originalText, 2000);
  });
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => {
  console.log('Survey: DOM loaded, initializing...');
  
  // Track start time
  if (!sessionStorage.getItem('survey_start_time')) {
    sessionStorage.setItem('survey_start_time', Date.now());
  }
  
  try {
    // Initialize survey and make it globally accessible
    window.storySurvey = new StorySurveyLogic();
    console.log('Survey: Initialized successfully');
    
    // Also bind to start button directly as backup
    const startBtn = document.querySelector('.start-button');
    if (startBtn) {
      startBtn.addEventListener('click', function(e) {
        e.preventDefault();
        if (window.storySurvey && window.storySurvey.nextQuestion) {
          window.storySurvey.nextQuestion();
        } else {
          console.error('Survey not ready yet');
          // Try to initialize if not ready
          if (!window.storySurvey) {
            window.storySurvey = new StorySurveyLogic();
          }
          setTimeout(() => {
            if (window.storySurvey && window.storySurvey.nextQuestion) {
              window.storySurvey.nextQuestion();
            }
          }, 100);
        }
      });
    }
  } catch (error) {
    console.error('Survey initialization failed:', error);
  }
});